import React, { useCallback, useEffect, useState } from 'react';
import './AlphaScanner.css';
import { getAlphaPainel } from '../api/brapi';
import { formatTradingDate } from '../utils/dates';

const fmt = (valor, sufixo = '', vazio = '—') => (valor != null && Number.isFinite(Number(valor))
  ? `${Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${sufixo}`
  : vazio);
const fmtReais = (valor, vazio = '—') => (valor != null && Number.isFinite(Number(valor))
  ? Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
  : vazio);
const fmtDataHora = iso => (iso ? new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '—');

const NOMES_MOTIVO = { rsi: 'RSI semanal', perda: 'Perda máxima', deslistagem: 'Deslistagem' };
const NOMES_TIPO = { entrada: 'Entrada', saida: 'Saída' };
// FAT-48: enquanto o backend calcula a validação (worker), o painel pergunta de novo algumas vezes.
const ESPERA_VALIDACAO_MS = 15000;
const MAX_TENTATIVAS_VALIDACAO = 8;

function TabelaComparacao({ comparacao, prefixo, testid }) {
  const linhas = [['Alpha', comparacao.alpha], ['IBOV', comparacao.ibov], ['CDI', comparacao.cdi]];
  return (
    <div className="as-tabela-wrap"><table className="as-tabela" data-testid={testid}>
      <thead>
        <tr><th>Série</th><th>Retorno total</th><th>Anualizado</th><th>Drawdown máx.</th></tr>
      </thead>
      <tbody>
        {linhas.map(([nome, m]) => (
          <tr key={nome} data-testid={`${prefixo}-${nome}`}>
            <td>{nome}</td>
            <td>{m ? fmt(m.retornoTotalPct, '%') : 'indisponível'}</td>
            <td>{m ? fmt(m.retornoAnualizadoPct, '%') : 'indisponível'}</td>
            <td>{m ? fmt(m.maxDrawdownPct, '%') : 'indisponível'}</td>
          </tr>
        ))}
      </tbody>
    </table></div>
  );
}

function Comparacao({ resultado }) {
  if (!resultado || resultado.situacao !== 'ok') {
    return (
      <p data-testid="alpha-painel-resultado-indisponivel">
        Resultado indisponível{resultado?.motivo ? `: ${resultado.motivo}` : '.'}
      </p>
    );
  }
  const honestas = resultado.estatisticasHonestas;
  return (
    <>
      <TabelaComparacao comparacao={resultado.comparacao} prefixo="alpha-comparacao" testid="alpha-painel-comparacao" />
      {honestas?.amostraInsuficiente && (
        <p role="note" data-testid="alpha-painel-amostra">
          Amostra insuficiente: mínimo de {honestas.minimoTrades} trades para estatísticas confiáveis.
        </p>
      )}
    </>
  );
}

function ResumoCarteira({ resumo, resultado }) {
  if (!resumo) return null;
  const temValores = resumo.capitalUtilizado != null;
  return (
    <div className="as-tabela-wrap"><table className="as-tabela" data-testid="alpha-carteira-resumo">
      <tbody>
        <tr data-testid="alpha-resumo-capital">
          <th>Capital utilizado</th>
          <td>{temValores ? `${fmtReais(resumo.capitalUtilizado)} (${fmt(resumo.capitalUtilizadoPct, '%')} de ${fmtReais(resumo.capitalInicial)})` : '—'}</td>
        </tr>
        <tr data-testid="alpha-resumo-vagas">
          <th>Vagas livres</th><td>{resumo.vagasLivres} de {resumo.maxPosicoes}</td>
        </tr>
        <tr data-testid="alpha-resumo-realizado">
          <th>Resultado realizado (líquido)</th>
          <td>{temValores ? `${fmtReais(resumo.realizado.reais)} (${fmt(resumo.realizado.pct, '%')}) em ${resumo.realizado.trades} operação(ões)` : '—'}</td>
        </tr>
        <tr data-testid="alpha-resumo-nao-realizado">
          <th>Resultado não realizado</th>
          <td>{temValores ? `${fmtReais(resumo.naoRealizado.reais)} (${fmt(resumo.naoRealizado.pct, '%')}), antes do custo de venda` : '—'}</td>
        </tr>
      </tbody>
    </table>
    {temValores
      ? <p className="as-rodape">Posição no fechamento de {formatTradingDate(resumo.dataBase)}.</p>
      : <p className="as-rodape" data-testid="alpha-resumo-indisponivel">Valores em R$ indisponíveis{resultado?.motivo ? `: ${resultado.motivo}` : '.'}</p>}
    </div>
  );
}

function ValidacaoBrasil({ validacao }) {
  if (!validacao || validacao.situacao === 'calculando') {
    return <p data-testid="alpha-validacao-calculando">Calculando o backtest com os dados da B3… a tela atualiza sozinha.</p>;
  }
  if (validacao.situacao !== 'ok') {
    return (
      <p data-testid="alpha-validacao-indisponivel">
        Validação indisponível{validacao.motivo ? `: ${validacao.motivo}` : '.'}
      </p>
    );
  }
  const { periodo } = validacao;
  return (
    <>
      <div className="as-tabela-wrap"><table className="as-tabela" data-testid="alpha-validacao">
        <tbody>
          <tr data-testid="alpha-validacao-periodo">
            <th>Período</th><td>{formatTradingDate(periodo.inicio)} a {formatTradingDate(periodo.fim)} ({periodo.pregoes} pregões)</td>
          </tr>
          <tr data-testid="alpha-validacao-anualizado"><th>Retorno anualizado</th><td>{fmt(validacao.retornoAnualizadoPct, '%')}</td></tr>
          <tr data-testid="alpha-validacao-drawdown"><th>Máximo drawdown</th><td>{fmt(validacao.maxDrawdownPct, '%')}</td></tr>
          <tr data-testid="alpha-validacao-operacoes"><th>Número de operações</th><td>{validacao.operacoes}</td></tr>
          <tr data-testid="alpha-validacao-acerto">
            <th>Taxa de acerto</th>
            <td>
              {validacao.amostraInsuficiente
                ? <span>{fmt(validacao.taxaAcertoPct, '%')} (amostra insuficiente)</span>
                : <strong>{fmt(validacao.taxaAcertoPct, '%')}</strong>}
            </td>
          </tr>
        </tbody>
      </table></div>
      <TabelaComparacao comparacao={validacao.comparacao} prefixo="alpha-validacao-comparacao" testid="alpha-validacao-comparacao" />
      {validacao.amostraInsuficiente && (
        <p role="note" data-testid="alpha-validacao-amostra">
          Amostra insuficiente: menos de {validacao.minimoTrades} operações; a taxa de acerto não é estatisticamente confiável.
        </p>
      )}
    </>
  );
}

export default function AlphaPainel() {
  const [painel, setPainel] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [tentativas, setTentativas] = useState(0);

  const carregar = useCallback(ativo => getAlphaPainel()
    .then(r => { if (ativo()) setPainel(r); })
    .catch(err => { if (ativo()) setErro(err?.response?.data?.error || err.message || 'Falha ao carregar o painel Alpha.'); })
    .finally(() => { if (ativo()) setCarregando(false); }), []);

  useEffect(() => {
    let ativo = true;
    carregar(() => ativo);
    return () => { ativo = false; };
  }, [carregar]);

  const calculando = painel?.validacao?.situacao === 'calculando';
  useEffect(() => {
    if (!calculando || tentativas >= MAX_TENTATIVAS_VALIDACAO) return undefined;
    let ativo = true;
    const t = setTimeout(() => { setTentativas(n => n + 1); carregar(() => ativo); }, ESPERA_VALIDACAO_MS);
    return () => { ativo = false; clearTimeout(t); };
  }, [calculando, tentativas, carregar]);

  const ranking = painel?.ranking || [];
  const posicoes = painel?.posicoes || [];
  const saidas = painel?.saidas || [];
  const historico = painel?.historico || [];
  const validacao = painel?.validacao;
  return (
    <section data-testid="alpha-painel" className="as-secao">
      <h2>Alpha · painel</h2>
      {painel?.aviso && <p className="as-nota" data-testid="alpha-painel-aviso">{painel.aviso}</p>}
      {carregando && <p data-testid="alpha-painel-carregando">Carregando…</p>}
      {erro && <p role="alert" data-testid="alpha-painel-erro">{erro}</p>}
      {painel && (
        <>
          <h3>Ranking (menor volatilidade = 1)</h3>
          {ranking.length === 0 ? <p data-testid="alpha-painel-sem-ranking">Sem candidatas no ranking.</p> : (
            <div className="as-tabela-wrap"><table className="as-tabela" data-testid="alpha-painel-ranking">
              <thead><tr><th>#</th><th>Ativo</th><th>Volatilidade anualizada</th><th>Selecionada</th></tr></thead>
              <tbody>
                {ranking.map(l => (
                  <tr key={l.ativo} data-testid={`alpha-ranking-${l.ativo}`}>
                    <td>{l.ranking}</td>
                    <td>{l.ativo}</td>
                    <td>{fmt(l.volatilidade, '%', 'indisponível')}</td>
                    <td>{l.selecionada ? 'Sim' : 'Não'}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}

          <h3>Carteira Alpha (teórica)</h3>
          <p className="as-nota" data-testid="alpha-carteira-como-nasce">
            Como a carteira nasce: as posições são teóricas. Elas vêm das entradas sinalizadas na avaliação semanal (fechamento
            da semana encerrada) e a execução é simulada na abertura do pregão seguinte, com custos e slippage. A saída segue a
            mesma regra: RSI semanal acima do limite ou fechamento diário abaixo do limite de perda. O FatCat não executa ordens.
          </p>
          <ResumoCarteira resumo={painel.resumoCarteira} resultado={painel.resultado} />
          {posicoes.length === 0 ? <p data-testid="alpha-painel-sem-posicoes">Nenhuma posição aberta na carteira teórica.</p> : (
            <div className="as-tabela-wrap"><table className="as-tabela" data-testid="alpha-painel-posicoes">
              <thead>
                <tr>
                  <th>Ativo</th><th>Data de entrada</th><th>Preço de entrada</th><th>Resultado atual</th><th>RSI semanal</th>
                  <th>Próxima ação</th><th>Limite de perda</th>
                </tr>
              </thead>
              <tbody>
                {posicoes.map(p => (
                  <tr key={p.ativo} data-testid={`alpha-posicao-${p.ativo}`}>
                    <td>{p.ativo}</td>
                    <td>{formatTradingDate(p.dataEntrada)}</td>
                    <td>{fmt(p.precoEntrada)}</td>
                    <td data-testid={`alpha-posicao-resultado-${p.ativo}`}>{fmt(p.retornoPct, '%')}</td>
                    <td>{fmt(p.rsiSemanal)}</td>
                    <td data-testid={`alpha-posicao-acao-${p.ativo}`}>{p.proximaAcao || 'Aguardar'}</td>
                    <td>{fmt(p.limitePerda)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}

          <h3>Saídas</h3>
          {saidas.length === 0 ? <p data-testid="alpha-painel-sem-saidas">Nenhuma saída registrada.</p> : (
            <div className="as-tabela-wrap"><table className="as-tabela" data-testid="alpha-painel-saidas">
              <thead><tr><th>Ativo</th><th>Data</th><th>Motivo</th><th>P&amp;L líquido</th></tr></thead>
              <tbody>
                {saidas.map(s => (
                  <tr key={`${s.ativo}-${s.data}`} data-testid={`alpha-saida-${s.ativo}`}>
                    <td>{s.ativo}</td>
                    <td>{formatTradingDate(s.data)}</td>
                    <td>{NOMES_MOTIVO[s.motivo] || s.motivo} ({s.motivo})</td>
                    <td>{fmt(s.pnlPct, '%', 'indisponível')}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}

          <h3>Histórico dos sinais</h3>
          {historico.length === 0 ? (
            <p data-testid="alpha-painel-sem-historico">
              Nenhum sinal registrado ainda. O histórico começa no início dos registros, sem dados retroativos.
            </p>
          ) : (
            <div className="as-tabela-wrap"><table className="as-tabela" data-testid="alpha-painel-historico">
              <thead><tr><th>Semana</th><th>Data do sinal</th><th>Ativo</th><th>Tipo</th><th>Motivo</th></tr></thead>
              <tbody>
                {historico.map(r => (
                  <tr key={`${r.semana}-${r.ativo}-${r.tipo}`} data-testid={`alpha-historico-${r.semana}-${r.ativo}-${r.tipo}`}>
                    <td>{formatTradingDate(r.semana)}</td>
                    <td>{formatTradingDate(r.dataSinal)}</td>
                    <td>{r.ativo}</td>
                    <td>{NOMES_TIPO[r.tipo] || r.tipo}</td>
                    <td>{r.motivo || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}

          <h3>Resultado da carteira vs IBOV e CDI</h3>
          <Comparacao resultado={painel.resultado} />

          <h3>Validação no Brasil</h3>
          <p className="as-nota">
            Backtest da regra Alpha com os dados da B3 do FatCat (execução simulada na abertura seguinte, custos, slippage e caixa
            remunerado pelo CDI do Banco Central).
          </p>
          <ValidacaoBrasil validacao={validacao} />
          <p role="note" data-testid="alpha-validacao-aviso">
            Aviso: resultado passado não garante resultado futuro. O FatCat não executa ordens.
          </p>
          <p className="as-rodape" data-testid="alpha-validacao-atualizacao">
            Atualizado em {fmtDataHora(validacao?.atualizadoEm)}{validacao?.dataDados ? ` · dados até ${formatTradingDate(validacao.dataDados)}` : ''}.
          </p>
        </>
      )}
    </section>
  );
}
